import { FlashList } from '@shopify/flash-list';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import { AlphaSortSlider } from '@components/AlphaSortSlider/AlphaSortSlider';
import EmptyContactListPlaceHolder from '@components/EmptyState/EmptyContactListPlaceHolder';
import EmptyStateNoContactGroup from '@components/EmptyState/EmptyStateNoContactGroup';
import EmptyStateNoSearch from '@components/EmptyState/EmptyStateNoSearch';
import { ListGroupData } from '@components/Invite/SelectMembersList';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import {
  MarginTopM,
  MarginBottomListFooter,
  PaddingBottomListFooter,
} from '@components/shared/ListSpacers';
import { useContactListSpacers } from '@hooks/useContactListSpacers';
import theme from '@themes/theme';

import Contact, { ContactType } from './Contact';

interface ContactListMobileProps {
  contacts: ListGroupData[];
  taskTagUsers?: ContactType[];
  onPress: (item: ContactType) => void;
  filterVal?: string;
  alphabetize?: boolean;
  headerSpace?: keyof typeof theme.spacing;
  footerSpace?: number;
  isEditing?: boolean;
  isInviting?: boolean;
  loading?: boolean;
  selected?: string[];
  shouldDisplayPhoneContactLabel?: boolean;
  disabledRoles?: string[];
  showMessageIcon?: boolean;
  onMessagePress?: (item: ContactType) => void;
  showEmptyStateNoSearch?: boolean;
}

const ESTIMATED_CONTACT_ITEM_SIZE = 40;

const ContactListMobile: React.FC<ContactListMobileProps> = ({
  contacts,
  taskTagUsers,
  filterVal,
  alphabetize,
  onPress,
  headerSpace,
  footerSpace,
  isEditing,
  isInviting,
  loading,
  selected,
  shouldDisplayPhoneContactLabel,
  disabledRoles,
  showMessageIcon = false,
  showEmptyStateNoSearch = false,
  onMessagePress,
}) => {
  const { t } = useTranslation();
  const [filteredContacts, setFilteredContacts] =
    useState<ListGroupData[]>(contacts);
  const [alphabetizedSectionList, setAlphabetizedSectionList] = useState<
    (ListGroupData | string)[]
  >([]);
  const [alphabetizeList, setAlphabetizeList] = useState<string[]>([]);
  const flashListRef = useRef<FlashList<string>>(null);
  const [selectedContact, setSelectedContact] = useState<
    ListGroupData | undefined
  >();

  const [friendRequestSuccess, setFriendRequestSuccess] = useState(false);
  const taskTagUserIds = taskTagUsers?.map((i) => i.id);

  const { renderListHeader, renderListFooter } = useContactListSpacers({
    headerSpace,
    footerSpace,
    showEmptyStateNoSearch,
  });

  useEffect(() => {
    const filtered = filterVal
      ? contacts.filter((contact) =>
          contact?.name.toLowerCase().includes(filterVal.toLowerCase())
        )
      : contacts;

    // Set both filtered and complete contact lists

    setFilteredContacts(filtered);
  }, [contacts, filterVal]);

  useEffect(() => {
    // Only call when filteredContacts change, not on length alone
    if (filteredContacts.length > 0 || filterVal !== '') {
      getAlphabetizedSectionList();
    }
  }, [filteredContacts]);

  const sortContacts = () => {
    const numsSpecials: ListGroupData[] = []; // numbers and ASCII before A
    const letters: ListGroupData[] = []; // letters
    const asterisks: ListGroupData[] = []; // ASCII after Z

    filteredContacts.forEach((s) => {
      const firstChar = s.name?.[0];
      if (!firstChar) return;

      const charCode = firstChar.charCodeAt(0);

      if (/[a-z]/i.test(firstChar)) {
        letters.push(s); // Letters A-Z or a-z
      } else if (charCode < 65) {
        numsSpecials.push(s); // Characters before 'A'
      } else {
        asterisks.push(s); // Characters after 'Z'
      }
    });

    // Helper function to sort by displayName's first character's char code
    const sortByCharCode = (arr: ListGroupData[]) =>
      arr.sort((a, b) => a?.name[0].charCodeAt(0) - b.name[0].charCodeAt(0));

    // Sort each category
    sortByCharCode(numsSpecials);
    letters.sort((a, b) =>
      a.name[0].toUpperCase().localeCompare(b.name[0].toUpperCase())
    );
    sortByCharCode(asterisks);

    return { letters, numsSpecials, asterisks };
  };

  const getAlphabetizedSectionList = () => {
    const tmpAlphabetizeList = ['#'];
    const { letters, numsSpecials, asterisks } = sortContacts();

    const formattedAlpha: (ListGroupData | string)[] = [];
    const formattedNumSpecial: (ListGroupData | string)[] = numsSpecials.length
      ? ['#', ...numsSpecials]
      : [];
    const formattedAsterisk: (ListGroupData | string)[] = asterisks.length
      ? ['*', ...asterisks]
      : [];

    letters.forEach((obj) => {
      const key = obj.name.charAt(0).toUpperCase();

      // Check if the key is already added, if not, add it before the contact
      if (!formattedAlpha.includes(key)) {
        formattedAlpha.push(key);
      }
      formattedAlpha.push(obj);
    });

    // Add A-Z to the alphabetize list
    for (
      let charCode = 'A'.charCodeAt(0);
      charCode <= 'Z'.charCodeAt(0);
      charCode++
    ) {
      tmpAlphabetizeList.push(String.fromCharCode(charCode));
    }

    // Add asterisk section to the alphabetize list
    tmpAlphabetizeList.push('*');

    setAlphabetizeList(tmpAlphabetizeList);
    setAlphabetizedSectionList([
      ...formattedNumSpecial,
      ...formattedAlpha,
      ...formattedAsterisk,
    ]);
  };
  if (loading) return <ActivityIndicatorLoading />;
  const toggleFriendRequestSuccess = (friendRequest: boolean) => {
    setFriendRequestSuccess(friendRequest);
  };

  const renderContact = (item: ContactType, disabled?: boolean) => {
    const isTaskTagUser = taskTagUserIds?.includes(item.id);
    return (
      <Contact
        key={item.id}
        toggleFriendRequestSuccess={
          isTaskTagUser ? toggleFriendRequestSuccess : () => {}
        }
        isTaskTagUser={isTaskTagUser}
        isContactGroup={true}
        contact={{ ...item, roleLabel: item.roleLabel }}
        filterVal={filterVal}
        onPress={() => {
          if (!isEditing) {
            setSelectedContact((current) => {
              return current?.id === item.id ? undefined : item;
            });
          }
          onPress({
            ...item,
            type: 'GROUP',
            memberCount: item?.users?.length,
            users: item?.users,
          });
        }}
        isEditing={isEditing}
        isSelected={selected?.some((i) => i === item.id) || item.isAssigned}
        isInviting={isInviting}
        shouldDisplayPhoneContactLabel={shouldDisplayPhoneContactLabel}
        disabled={disabled || item.isAssigned || isTaskTagUser}
        showMessageIcon={showMessageIcon}
        onMessagePress={() => onMessagePress?.(item)}
      />
    );
  };

  const contactsAndUsersList = () => {
    return [
      ...filteredContacts,
      t('shared:moreOnTaskTag'),
      ...(taskTagUsers || []),
    ];
  };

  if (taskTagUsers && taskTagUsers.length > 0) {
    // section list

    return (
      <View style={{ flex: 1 }}>
        <FlashList
          showsVerticalScrollIndicator={false}
          keyboardDismissMode='on-drag'
          ItemSeparatorComponent={MarginTopM}
          ListFooterComponent={MarginBottomListFooter}
          data={contactsAndUsersList()}
          renderItem={({ item }) => {
            if (typeof item === 'string') {
              return (
                <Box
                  mt='s'
                  mb='s'
                  marginHorizontal='m'
                  pt='l'
                  borderTopWidth={1}
                  borderColor='grey02'>
                  <Text variant='labelSmall'>{item}</Text>
                </Box>
              );
            } else {
              return renderContact(item);
            }
          }}
          getItemType={(item) => {
            // To achieve better performance, specify the type based on the item
            return typeof item === 'string' ? 'sectionHeader' : 'row';
          }}
          estimatedItemSize={ESTIMATED_CONTACT_ITEM_SIZE}
        />

        {friendRequestSuccess && (
          <View style={{ marginBottom: theme.spacing.l }}>
            <Box
              backgroundColor='black'
              opacity={0.8}
              paddingHorizontal='s'
              paddingVertical='xs'
              borderRadius='xs'
              alignSelf='center'>
              <Text
                color='white'
                variant='labelSmall'
                lineHeight={16}
                textAlign='center'>
                {t('models:chat.connectionRequestSend')}
              </Text>
            </Box>
          </View>
        )}
      </View>
    );
  }

  return (
    <>
      {alphabetize ? (
        <Box flex={1} position='relative'>
          <FlashList
            ref={flashListRef}
            extraData={selected}
            showsVerticalScrollIndicator={false}
            estimatedItemSize={ESTIMATED_CONTACT_ITEM_SIZE}
            keyboardDismissMode='on-drag'
            ItemSeparatorComponent={MarginTopM}
            ListFooterComponent={PaddingBottomListFooter}
            ListEmptyComponent={
              showEmptyStateNoSearch ? (
                <Box
                  px='l'
                  style={{ marginTop: -theme.spacing.listFooter / 2 }}>
                  <EmptyStateNoSearch showSmall />
                </Box>
              ) : (
                <Box px='l'>
                  <EmptyContactListPlaceHolder showSmall />
                </Box>
              )
            }
            data={alphabetizedSectionList}
            renderItem={({ item }) => {
              if (typeof item === 'string') {
                return (
                  <Box
                    backgroundColor='grey01'
                    paddingVertical='xs'
                    paddingLeft='m'>
                    <Text color='textPrimary' variant='labelEmphasized'>
                      {item}
                    </Text>
                  </Box>
                );
              }
              // On the web, keep clicking after clicking the contact
              else if (onMessagePress) {
                return renderContact(item, false);
              } else {
                const disabled =
                  (!!item.roleLabel &&
                    disabledRoles?.includes(item.roleLabel)) ||
                  selectedContact?.id === item.id;

                return renderContact(item, disabled);
              }
            }}
            getItemType={(item) => {
              // To achieve better performance, specify the type based on the item
              return typeof item === 'string' ? 'sectionHeader' : 'row';
            }}
          />
          {alphabetizedSectionList.length > 0 && (
            <AlphaSortSlider
              flashListRef={flashListRef}
              alphabetizeList={alphabetizeList}
            />
          )}
        </Box>
      ) : (
        <FlashList
          showsVerticalScrollIndicator={false}
          extraData={selected}
          estimatedItemSize={ESTIMATED_CONTACT_ITEM_SIZE}
          keyboardDismissMode='on-drag'
          data={filteredContacts}
          renderItem={({ item }) => renderContact(item)}
          ItemSeparatorComponent={MarginTopM}
          ListHeaderComponent={renderListHeader}
          ListFooterComponent={renderListFooter}
          ListEmptyComponent={
            showEmptyStateNoSearch ? (
              <Box px='l' style={{ marginTop: -theme.spacing.listFooter / 2 }}>
                <EmptyStateNoSearch showSmall />
              </Box>
            ) : (
              <Box px='l'>
                <EmptyStateNoContactGroup showSmall />
              </Box>
            )
          }
        />
      )}
    </>
  );
};

export default ContactListMobile;
